import React from 'react';
import { Ban } from 'lucide-react';
import type { TaskSignatureBypassResponse } from '../../services/api/types.gen';
import { bypassActorName } from './signatureSummary';

interface BypassCardProps {
  bypass: TaskSignatureBypassResponse;
}

function formatTimestamp(iso: string): string {
  const dt = new Date(iso);
  // Fixed UTC so every viewer sees the same instant, matching SignatureCard.
  const formatted = dt.toLocaleString('en-US', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return `${formatted} UTC`;
}

/**
 * Renders a forfeited clinician signature in the chart's signing history.
 *
 * Deliberately not a SignatureCard variant. A bypass is the absence of a
 * signature, and every affordance here says so: no check mark, no
 * "Electronically Signed By", no signer role. The wording matches the
 * 30-Day Summary PDF's "Signature bypassed - {reason} - authorized by
 * {actor}" so screen and paper describe the same event the same way.
 */
export const BypassCard: React.FC<BypassCardProps> = ({ bypass }) => {
  const metadata =
    (bypass.bypass_metadata as Record<string, unknown> | null | undefined) ??
    {};
  const actorName = bypassActorName(bypass);
  const credentials = metadata.actor_credentials as string | undefined | null;
  const reasonLabel = bypass.reason_label ?? bypass.reason;
  const superseded = !!bypass.superseded_at;
  const hasIntegrity = !!bypass.content_hash_hex;

  const actorLine = credentials ? `${actorName}, ${credentials}` : actorName;

  return (
    <div className='rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2.5'>
      <div className='flex items-start gap-2'>
        <Ban className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700' />
        <div className='flex-1 min-w-0'>
          <div className='text-[11px] uppercase tracking-wide text-amber-700'>
            Clinician Signature Bypassed
          </div>
          <div
            className={`text-sm font-medium text-amber-900 ${
              superseded ? 'line-through' : ''
            }`}
          >
            {reasonLabel}
          </div>
          <div className='text-xs text-amber-700'>
            Authorized by {actorLine}
          </div>
          {bypass.comments ? (
            <div className='text-xs text-amber-700'>{bypass.comments}</div>
          ) : null}
          <div className='text-xs text-amber-700'>
            {formatTimestamp(bypass.bypassed_at)}
          </div>
          <div className='text-[11px] font-mono text-amber-700'>
            Bypass ID: {bypass.id.slice(0, 8)}
            {hasIntegrity ? ' · hmac-sha256' : ''}
          </div>
          {superseded ? (
            <div className='mt-1 text-[11px] text-amber-800'>
              No longer applies — the chart was reopened, so the signature is
              owed again.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
